import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

if (!customElements.get('lalorene-free-shipping')) {
  const selector = 'lalorene-free-shipping';

  let timer;
  let controller;
  let generation = 0;
  let pending = 0;

  const bars = () =>
    Array.from(document.querySelectorAll(selector));

  function invalidate() {
    generation += 1;
    clearTimeout(timer);
    controller?.abort();
  }

  function schedule() {
    invalidate();

    if (pending === 0) {
      timer = setTimeout(refresh, 60);
    }
  }

  async function refresh() {
    const targets = bars();

    if (!targets.length || pending > 0) return;

    const version = generation;
    const request = new AbortController();

    controller = request;

    const timeout = setTimeout(() => {
      request.abort();
    }, 10000);

    try {
      const response = await fetch(targets[0].dataset.cartUrl, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        },
        signal: request.signal
      });

      if (!response.ok) {
        throw new Error('Cart unavailable');
      }

      const cart = await response.json();

      if (version !== generation || pending > 0) return;

      if (
        !Number.isFinite(cart.total_price) ||
        !Number.isFinite(cart.item_count) ||
        typeof cart.currency !== 'string'
      ) {
        throw new Error('Invalid cart response');
      }

      bars().forEach((bar) => {
        bar.paint(cart);
      });
    } catch (error) {
      if (version === generation && pending === 0) {
        bars().forEach((bar) => {
          bar.unavailable();
        });
      }
    } finally {
      clearTimeout(timeout);
    }
  }

  class LaloreneFreeShipping extends HTMLElement {
    connectedCallback() {
      schedule();
    }

    paint(cart) {
      const message = this.querySelector('[data-message]');
      const track = this.querySelector('[data-track]');
      const fill = this.querySelector('[data-fill]');

      if (!message || !track || !fill) return;

      this.removeAttribute('aria-busy');

      const threshold = Number(this.dataset.threshold);

      if (
        cart.currency !== this.dataset.currency ||
        !(threshold > 0)
      ) {
        message.textContent =
          'Worldwide free shipping on qualifying orders. Eligibility is confirmed at checkout.';

        track.hidden = true;
        return;
      }

      const total = Math.max(0, cart.total_price);
      const remaining = Math.max(0, threshold - total);
      const progress = Math.min(100, (total / threshold) * 100);

      const money = new Intl.NumberFormat(
        document.documentElement.lang || 'en',
        {
          style: 'currency',
          currency: cart.currency,
          currencyDisplay: 'code'
        }
      );

      track.hidden = false;

      track.setAttribute(
        'aria-valuenow',
        String(Math.round(progress))
      );

      fill.style.width = `${progress}%`;

      if (cart.item_count === 0) {
        message.textContent =
          `Enjoy worldwide FREE SHIPPING on orders ${money.format(
            threshold / 100
          )}+.`;
      } else if (remaining > 0) {
        const amount = document.createElement('strong');

        amount.textContent = money.format(remaining / 100);

        message.replaceChildren(
          'You’re ',
          amount,
          ' away from FREE SHIPPING.'
        );
      } else {
        message.textContent =
          'You’ve reached the worldwide FREE SHIPPING minimum!';
      }
    }

    unavailable() {
      this.removeAttribute('aria-busy');

      const message = this.querySelector('[data-message]');
      const track = this.querySelector('[data-track]');

      if (message) {
        message.textContent =
          'Worldwide free shipping on qualifying orders. Eligibility is confirmed at checkout.';
      }

      if (track) {
        track.hidden = true;
      }
    }
  }

  customElements.define(selector, LaloreneFreeShipping);

  document.addEventListener(
    StandardEvents.cartLinesUpdate,
    (event) => {
      invalidate();

      bars().forEach((bar) => {
        bar.setAttribute('aria-busy', 'true');
      });

      if (
        event.promise &&
        typeof event.promise.then === 'function'
      ) {
        pending += 1;

        const settled = () => {
          pending -= 1;
          schedule();
        };

        Promise.resolve(event.promise).then(settled, settled);
      } else {
        schedule();
      }
    }
  );

  document.addEventListener(
    DrawerOpenEvent.eventName,
    schedule,
    true
  );

  document.addEventListener('shopify:section:load', schedule);

  document.addEventListener('lalorene:cart-refresh', schedule);

  window.addEventListener('pageshow', schedule);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      schedule();
    }
  });
}